import {
  HttpClient,
  HttpEventType,
  HttpHeaders
} from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { custom } from 'devextreme/ui/dialog';
import { saveAs } from 'file-saver';
import { CookieService } from 'ngx-cookie-service';

import { AttachmentService } from '../../../providers/attachment.service';
import { BaseComponent } from '../../shared/base/base.component';

@Component({
  selector: 'app-uploader',
  templateUrl: './uploader.component.html',
  styleUrls: ['./uploader.component.scss']
})
export class UploaderComponent extends BaseComponent implements OnInit {

  @Input() path: any;
  @Input() selectedRows: any;
  @Input() pkey: any;
  @Input() header = true;
  @Input() uploadFrom = true;
  @Input() ref: any;

  url: string = this.service.url;

  public hasBaseDropZoneOver = false;

  attachment: any[] = [];

  caption = '';
  description = '';

  public selectedAccountRow: any;

  // New upload properties
  selectedFile: File | null = null;
  uploadProgress = 0;
  uploading = false;

  constructor(
    public attachmentService: AttachmentService,
    public translator: TranslateService,
    public activeModal: NgbActiveModal,
    public cookieService: CookieService,
    public titleService: Title,
    private http: HttpClient
  ) {
    super(
      attachmentService,
      translator,
      cookieService,
      titleService
    );
  }

  async ngOnInit() {
    this.getAttachment();
  }

  /**
   * File drag/drop area
   */
  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  /**
   * Called when the user selects a file.
   *
   * Use this with:
   *
   * <input
   *   type="file"
   *   (change)="onFileSelected($event)"
   * >
   */
  onFileSelected(event: any): void {
    const files = event.target.files;

    if (!files || !files.length) {
      this.selectedFile = null;
      return;
    }

    // Only use the first file, same behavior as your old uploader.
    this.selectedFile = files[0];

    // Automatically use filename as caption.
    this.caption = this.selectedFile.name
      .split('.')
      .slice(0, -1)
      .join('.');

    // If there is no extension
    if (!this.caption) {
      this.caption = this.selectedFile.name;
    }

    this.uploadProgress = 0;
  }

  /**
   * Optional support for drag/drop.
   *
   * If your HTML uses ng2-file-upload's old
   * fileOver/drop directives, replace them with
   * a normal HTML5 drop handler and call this method.
   */
  onFileDrop(event: DragEvent): void {
    event.preventDefault();

    this.hasBaseDropZoneOver = false;

    if (!event.dataTransfer || !event.dataTransfer.files.length) {
      return;
    }

    this.selectedFile = event.dataTransfer.files[0];

    this.caption = this.selectedFile.name
      .split('.')
      .slice(0, -1)
      .join('.');

    if (!this.caption) {
      this.caption = this.selectedFile.name;
    }

    this.uploadProgress = 0;
  }

  /**
   * Prevent browser from opening the dropped file.
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.hasBaseDropZoneOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.hasBaseDropZoneOver = false;
  }

  /**
   * Upload selected file.
   */
  send(): void {
    if (!this.selectedFile) {
      this.handleError('EMPTY_UPLOAD');
      return;
    }

    if (!this.caption || this.caption.trim().length === 0) {
      this.handleError('EMPTY_UPLOAD_CAPTION');
      return;
    }

    if (this.uploading) {
      return;
    }

    let uploadPath: string;

    if (this.pkey !== null && this.pkey !== undefined) {
      uploadPath =
        this.path +
        '/' +
        (this.ref || this.selectedRows[this.pkey]);
    } else {
      uploadPath =
        this.path +
        '/' +
        this.selectedRows;
    }

    const formData = new FormData();

    // IMPORTANT:
    // Keep these BEFORE the file.
    formData.append('path', uploadPath);

    if (this.pkey !== null && this.pkey !== undefined) {
      formData.append('pkey', String(this.pkey));
    }

    formData.append('caption', this.caption || '');
    formData.append('DESCRIPTION', this.description || '');

    // Backend uses multer.any(), so "file" is fine.
    formData.append(
      'file',
      this.selectedFile,
      this.selectedFile.name
    );

    this.uploading = true;
    this.uploadProgress = 0;

    const headers = new HttpHeaders({
      token: this.token
    });

    this.http.post(this.uploadUrl, formData, {
      headers,
      reportProgress: true,
      observe: 'events',
      responseType: 'text'
    }).subscribe({

      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {

          if (event.total) {
            this.uploadProgress = Math.round(
              event.loaded * 100 / event.total
            );
          }

          return;
        }

        if (event.type === HttpEventType.Response) {

          this.uploading = false;
          this.uploadProgress = 100;

          this.clear();
          this.attachment = JSON.parse(event.body);
        }
      },

      error: (error) => {
        console.error('UPLOAD ERROR');
        console.error('status:', error.status);
        console.error('error:', error.error);
        console.error('message:', error.message);

        this.uploading = false;
        this.uploadProgress = 0;

        this.handleError(
          error?.error ||
          error?.message ||
          'UPLOAD_ERROR'
        );
      }
    });
  }

  /**
   * Get attachments.
   */
  getAttachment(): void {

    this.selectedAccountRow =
      this.ref ||
      (
        this.pkey !== null &&
          this.pkey !== undefined
          ? this.selectedRows[this.pkey]
          : this.selectedRows
      );

    if (this.pkey === null) {
      this.selectedAccountRow = this.selectedRows;
    }

    this.attachment = [];

    const path =
      this.path +
      '/' +
      this.selectedAccountRow;

    this.attachmentService
      .getAttachment(path)
      .subscribe((data: any) => {
        if (!data.err) {
          this.attachment = data;
        }
      });
  }

  /**
   * Delete file.
   */
  deleteFile(item: any): void {

    let path =
      this.path +
      '/' +
      (this.ref || this.selectedRows[this.pkey]) +
      '/' +
      item;

    if (this.pkey === null) {
      path =
        this.path +
        '/' +
        this.selectedRows +
        '/' +
        item;
    }

    this.attachmentService
      .deleteFile(path, this.pkey)
      .subscribe((data: any) => {
        if (!data.err) {
          this.attachment = data;
        }
      });
  }

  /**
   * Restore file.
   */
  restore(item: any): void {

    let path =
      this.path +
      '/' +
      (this.ref || this.selectedRows[this.pkey]) +
      '/' +
      item;

    if (this.pkey === null) {
      path =
        this.path +
        '/' +
        this.selectedRows +
        '/' +
        item;
    }

    this.attachmentService
      .restore(path, this.pkey)
      .subscribe((data: any) => {
        if (!data.err) {
          this.attachment = data;
        }
      });
  }

  /**
   * Permanently delete file.
   */
  deleteForEver(item: any): void {

    let path =
      this.path +
      '/' +
      (this.ref || this.selectedRows[this.pkey]) +
      '/' +
      item;

    if (this.pkey === null) {
      path =
        this.path +
        '/' +
        this.selectedRows +
        '/' +
        item;
    }

    this.attachmentService
      .deleteForEver(path, this.pkey)
      .subscribe((data: any) => {
        if (!data.err) {
          this.attachment = data;
        }

      });
  }

  /**
   * Delete confirmation dialog.
   */
  deleteConfirm(
    item: any,
    deleteForEver = 'USER'
  ): void {

    const result = custom({
      title: this.translator.instant('DELETE'),

      message: this.translator.instant(
        'CONFIRM_ACTION'
      ),

      buttons: [
        {
          text: this.translator.instant('YES'),

          onClick: function () {
            return true;
          }
        },
        {
          text: this.translator.instant('NO'),

          onClick: function () {
            return false;
          }
        }
      ]
    });

    result.show().done((dialogResult) => {

      if (!dialogResult) {
        result.hide();
        return;
      }

      if (deleteForEver === 'USER') {
        this.deleteFile(item);
      }

      if (deleteForEver === 'ADMIN') {
        this.deleteForEver(item);
      }

    });
  }

  /**
   * Download file.
   */
  downloadFile(item: any): void {

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      token: this.token
    });

    let path =
      `${this.url}api/files/${this.path}/` +
      `${this.ref || this.selectedRows[this.pkey]}/${item}` +
      `?token=${this.token}`;

    if (this.pkey === null) {
      path =
        `${this.url}api/files/${this.path}/` +
        `${this.selectedRows}/${item}` +
        `?token=${this.token}`;
    }

    this.attachmentService
      .getFile(path, {
        responseType: 'blob',
        headers: headers
      })
      .subscribe((data: any) => {
        this.saveFile(data, item);
      });
  }

  /**
   * Save downloaded file.
   */
  saveFile(
    blobContent: Blob,
    fileName: string
  ): void {

    const blob = new Blob(
      [blobContent],
      {
        type: 'application/octet-stream'
      }
    );

    saveAs(blob, fileName);
  }

  /**
   * File icon.
   */
  getFileIcon(ext: string): string {

    switch (ext?.toLowerCase()) {

      case 'jpeg':
      case 'jpg':
      case 'png':
      case 'gif':
        return 'fa fa-lg fa-file-picture-o';

      case 'pdf':
        return 'fa fa-lg fa-file-pdf-o';

      case 'txt':
      case 'text':
        return 'fa fa-lg fa-file-text-o';

      case 'doc':
      case 'docx':
        return 'fa fa-lg fa-file-word-o';

      case 'xls':
      case 'xlsx':
        return 'fa fa-lg fa-file-excel-o';

      case 'zip':
      case 'rar':
        return 'fa fa-lg fa-file-zip-o';

      default:
        return 'fa fa-lg fa-file';
    }
  }

  /**
   * Clear current upload.
   */
  clear(): void {

    this.selectedAccountRow = null;

    this.selectedFile = null;

    this.uploadProgress = 0;
    this.uploading = false;

    this.description = '';
    this.caption = '';
  }

}
